import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, RefreshCw, BookOpen, X, ExternalLink, FileJson, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  title: '',
  excerpt: '',
  content: '',
  cover_image: '',
  category: 'General',
  products: [],
  published: true,
};

const EMPTY_PRODUCT = { name: '', image: '', price_text: '', url: '', note: '' };

export default function GuidesPanel() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/guides/admin/list`);
      setGuides(r.data || []);
    } catch (e) {
      toast.error('Error cargando guías: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => {
    setForm(EMPTY);
    setEditingId(null);
    setShowForm(true);
  };

  const normalizeProduct = (p) => ({
    name: p.name || p.title || '',
    image: p.image || p.img || p.image_url || '',
    price_text: p.price_text || p.price || p.precio || '',
    url: p.url || p.link || p.enlace || '',
    note: p.note || p.nota || '',
  });

  const doImport = () => {
    if (!importText.trim()) {
      toast.error('Pega primero el JSON de la guía');
      return;
    }
    // Normaliza comillas tipográficas (" " ' ') → rectas y limpia texto sobrante
    const normalized = importText
      .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
      .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
      .trim();
    let data;
    try {
      data = JSON.parse(normalized);
    } catch (e) {
      try {
        const start = normalized.indexOf('{');
        const end = normalized.lastIndexOf('}');
        data = JSON.parse(normalized.slice(start, end + 1));
      } catch (e2) {
        toast.error('JSON inválido: revisa comas, comillas y llaves');
        return;
      }
    }
    if (Array.isArray(data)) data = data[0];
    if (!data || typeof data !== 'object') {
      toast.error('El JSON no tiene el formato esperado');
      return;
    }
    const products = Array.isArray(data.products)
      ? data.products.map(normalizeProduct)
      : [];
    setForm({
      title: data.title || data.titulo || '',
      excerpt: data.excerpt || data.summary || data.resumen || '',
      content: data.content || data.contenido || data.body || '',
      cover_image: data.cover_image || data.cover || data.image || data.portada || '',
      category: data.category || data.categoria || 'General',
      products,
      published: data.published !== false,
    });
    setEditingId(null);
    setShowImport(false);
    setImportText('');
    setShowForm(true);
    toast.success(`JSON cargado (${products.length} producto(s)). Revisa y pulsa "Crear guía".`);
  };

  const openEdit = (g) => {
    setForm({
      title: g.title || '',
      excerpt: g.excerpt || '',
      content: g.content || '',
      cover_image: g.cover_image || '',
      category: g.category || 'General',
      products: g.products || [],
      published: g.published !== false,
    });
    setEditingId(g.id);
    setShowForm(true);
  };

  const addProduct = () => setField('products', [...form.products, { ...EMPTY_PRODUCT }]);
  const setProduct = (i, k, v) =>
    setField(
      'products',
      form.products.map((p, idx) => (idx === i ? { ...p, [k]: v } : p))
    );
  const removeProduct = (i) =>
    setField('products', form.products.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error('La guía necesita un título');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/guides/${editingId}`, form);
        toast.success('Guía actualizada');
      } else {
        await axios.post(`${API}/guides`, form);
        toast.success('Guía creada');
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`¿Eliminar la guía "${title}"?`)) return;
    try {
      await axios.delete(`${API}/guides/${id}`);
      toast.success('Guía eliminada');
      load();
    } catch (e) {
      toast.error('No se pudo eliminar');
    }
  };

  return (
    <Card className="border-0 shadow-2xl mb-6" data-testid="guides-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-slate-900">
          <BookOpen size={20} className="text-indigo-600" />
          Guías / Blog
        </CardTitle>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" className="border-slate-300">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Button
            data-testid="guide-import-btn"
            onClick={() => {
              setShowImport((v) => !v);
              setShowForm(false);
            }}
            size="sm"
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            {showImport ? <X size={14} className="mr-1" /> : <FileJson size={14} className="mr-1" />}
            {showImport ? 'Cerrar' : 'Importar JSON'}
          </Button>
          <Button
            data-testid="guide-new-btn"
            onClick={showForm ? () => setShowForm(false) : openCreate}
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {showForm ? <X size={14} className="mr-1" /> : <Plus size={14} className="mr-1" />}
            {showForm ? 'Cerrar' : 'Nueva guía'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showImport && (
          <div
            className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 space-y-3"
            data-testid="guide-import-box"
          >
            <div className="flex items-center gap-2 text-emerald-800">
              <FileJson size={18} />
              <p className="text-sm font-semibold">Importar guía desde JSON</p>
            </div>
            <p className="text-xs text-emerald-700">
              Pega el JSON con las claves: <code>title</code>, <code>excerpt</code> (o{' '}
              <code>summary</code>), <code>category</code>, <code>cover_image</code>,{' '}
              <code>content</code> y <code>products</code> (array con name, price_text, image, url,
              note). Se rellenará el formulario para que revises antes de crear.
            </p>
            <Textarea
              data-testid="guide-import-textarea"
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              rows={10}
              className="font-mono text-xs bg-white"
              placeholder='Pega aquí el JSON de la guía...'
            />
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => {
                  setImportText('');
                  setShowImport(false);
                }}
                variant="outline"
                size="sm"
                className="border-slate-300"
              >
                Cancelar
              </Button>
              <Button
                data-testid="guide-import-load-btn"
                onClick={doImport}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Download size={14} className="mr-1" /> Cargar en el formulario
              </Button>
            </div>
          </div>
        )}

        {showForm && (
          <div className="bg-slate-50 rounded-lg p-4 mb-6 space-y-4" data-testid="guide-form">
            <div className="grid sm:grid-cols-[1fr_200px] gap-4">
              <div>
                <Label className="text-xs">Título *</Label>
                <Input
                  data-testid="guide-title-input"
                  value={form.title}
                  onChange={(e) => setField('title', e.target.value)}
                  placeholder="Cómo lijar pladur sin polvo"
                />
              </div>
              <div>
                <Label className="text-xs">Categoría</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setField('category', e.target.value)}
                  placeholder="Acabado, Lijado, Trucos..."
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Resumen (se ve en la tarjeta)</Label>
              <Input
                value={form.excerpt}
                onChange={(e) => setField('excerpt', e.target.value)}
                placeholder="Una frase que enganche al lector"
              />
            </div>
            <div>
              <Label className="text-xs">Imagen de portada (URL)</Label>
              <Input
                value={form.cover_image}
                onChange={(e) => setField('cover_image', e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label className="text-xs">Contenido (separa párrafos con una línea en blanco)</Label>
              <Textarea
                data-testid="guide-content-input"
                value={form.content}
                onChange={(e) => setField('content', e.target.value)}
                rows={8}
                placeholder="Escribe aquí el cuerpo de la guía..."
              />
            </div>

            {/* Affiliate products */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Herramientas recomendadas (afiliados)</Label>
                <Button
                  data-testid="guide-add-product"
                  onClick={addProduct}
                  size="sm"
                  variant="outline"
                  className="border-slate-300 h-7 text-xs"
                >
                  <Plus size={12} className="mr-1" /> Añadir producto
                </Button>
              </div>
              <div className="space-y-3">
                {form.products.map((p, i) => (
                  <div
                    key={i}
                    className="border border-slate-200 rounded-lg p-3 bg-white grid sm:grid-cols-2 gap-2 relative"
                  >
                    <Input
                      placeholder="Nombre del producto"
                      value={p.name}
                      onChange={(e) => setProduct(i, 'name', e.target.value)}
                    />
                    <Input
                      placeholder="Precio (texto, ej: 24,99 €)"
                      value={p.price_text}
                      onChange={(e) => setProduct(i, 'price_text', e.target.value)}
                    />
                    <Input
                      placeholder="URL imagen"
                      value={p.image}
                      onChange={(e) => setProduct(i, 'image', e.target.value)}
                    />
                    <Input
                      placeholder="Enlace de afiliado (Amazon...)"
                      value={p.url}
                      onChange={(e) => setProduct(i, 'url', e.target.value)}
                    />
                    <Input
                      className="sm:col-span-2"
                      placeholder="Nota corta (opcional)"
                      value={p.note}
                      onChange={(e) => setProduct(i, 'note', e.target.value)}
                    />
                    <button
                      onClick={() => removeProduct(i)}
                      className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full p-1 hover:bg-red-200"
                      aria-label="Quitar producto"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {form.products.length === 0 && (
                  <p className="text-xs text-slate-400">
                    Sin productos. Pulsa "Añadir producto" para recomendar herramientas con tu
                    enlace de afiliado.
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  data-testid="guide-published-switch"
                  checked={form.published}
                  onCheckedChange={(v) => setField('published', v)}
                />
                <Label className="text-xs">Publicada (visible en la web)</Label>
              </div>
              <Button
                data-testid="guide-save-btn"
                onClick={save}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Crear guía'}
              </Button>
            </div>
          </div>
        )}

        {guides.length === 0 ? (
          <p className="text-center text-slate-400 py-8 text-sm">
            No hay guías todavía. Crea la primera con "Nueva guía".
          </p>
        ) : (
          <div className="space-y-2" data-testid="guides-admin-list">
            {guides.map((g) => (
              <div
                key={g.id}
                data-testid={`guide-row-${g.id}`}
                className="flex flex-wrap items-center justify-between gap-3 border border-slate-100 rounded-lg p-3 hover:bg-slate-50"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-12 h-12 rounded bg-slate-100 bg-cover bg-center flex-shrink-0"
                    style={{
                      backgroundImage: g.cover_image ? `url(${g.cover_image})` : 'none',
                    }}
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{g.title}</p>
                    <p className="text-xs text-slate-400">
                      /guias/{g.slug} · {(g.products || []).length} producto(s)
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {g.published ? (
                    <Badge className="bg-green-100 text-green-700">Publicada</Badge>
                  ) : (
                    <Badge className="bg-slate-200 text-slate-600">Borrador</Badge>
                  )}
                  <a href={`/guias/${g.slug}`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="border-slate-300">
                      <ExternalLink size={14} />
                    </Button>
                  </a>
                  <Button
                    data-testid={`guide-edit-${g.id}`}
                    onClick={() => openEdit(g)}
                    variant="outline"
                    size="sm"
                    className="border-slate-300"
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    data-testid={`guide-delete-${g.id}`}
                    onClick={() => remove(g.id, g.title)}
                    variant="outline"
                    size="sm"
                    className="border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
